<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition duration-150 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition duration-100 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="visible && anchorEl"
        class="smart-search-dropdown-root fixed z-[9999] rounded-xl border border-black-15 bg-white shadow-xl"
        :class="dropdownClass"
        :style="dropdownStyle"
        @mouseleave="activeIndex = -1"
      >
        <div class="smart-search-dropdown-inner">
          <template v-if="loading">
            <div class="flex items-center justify-center py-10">
              <UIProgressSpinner
                width="24"
                height="24"
              />
            </div>
          </template>
          <template v-else-if="hasResults">
            <p
              v-if="isFallbackSuggestions"
              class="px-4 pt-3 pb-1 text-body-3 text-black-60"
            >
              {{ t('listings.smartSearchDropdown.noExactMatches') }}
            </p>
            <section
              v-if="displayProjects.length"
              class="px-3 py-2"
            >
              <p class="text-caption-1 mb-2 font-semibold uppercase tracking-wide text-black-50">
                {{ t('listings.smartSearchDropdown.projects') }}
              </p>
              <ul class="space-y-0.5">
                <li
                  v-for="project in displayProjects"
                  :key="project.id"
                  class="smart-search-row flex cursor-pointer items-center gap-3 rounded-lg px-2.5 py-2 transition-colors"
                  :style="isActive('project', project.id) ? activeRowStyle : {}"
                  @mouseenter="setActive('project', project.id)"
                  @mousedown.prevent="onSelect('project', project.id, project.title, project)"
                >
                  <div
                    v-if="project.image"
                    class="size-9 flex-shrink-0 overflow-hidden rounded-lg bg-black-10"
                  >
                    <NuxtImg
                      :src="project.image"
                      class="size-full object-cover"
                      width="36"
                      height="36"
                    />
                  </div>
                  <div
                    v-else
                    class="size-9 flex-shrink-0 rounded-lg bg-black-08 flex items-center justify-center"
                  >
                    <UIIcon
                      name="building-05"
                      class="text-black-40 text-lg"
                    />
                  </div>
                  <div class="min-w-0 flex-1">
                    <div class="flex min-w-0 items-center gap-2">
                      <p class="text-subhead-3 text-black-100 truncate">{{ project.title }}</p>
                      <!-- Same icon and gold as the Explore "100% Commission" chip -->
                      <span
                        v-if="project.isExclusive"
                        class="inline-flex flex-shrink-0 items-center gap-1 rounded-full border border-gold-primary-100 bg-gold-primary-20 px-2 py-0.5 text-[11px] font-semibold leading-4 text-black-100"
                        data-testid="smart-search-exclusive"
                      >
                        <UIIcon
                          name="lighting"
                          class="!size-3 text-gold-primary-120"
                        />
                        {{ t('exclusives.chip.label') }}
                      </span>
                    </div>
                    <p
                      v-if="project.developer"
                      class="text-caption-1 text-black-50 truncate"
                    >
                      {{ project.developer }}
                    </p>
                  </div>
                  <span
                    class="text-caption-1 text-gold-primary-100 hidden flex-shrink-0 font-medium tablet:inline-block"
                  >
                    {{ t('listings.smartSearchDropdown.openProject') }}
                    <span
                      aria-hidden="true"
                      class="rtl:inline-block rtl:-scale-x-100"
                      >→</span
                    >
                  </span>
                </li>
              </ul>
            </section>
            <section
              v-if="displayDevelopers.length"
              class="border-t border-black-10 px-3 py-2"
            >
              <p class="text-caption-1 mb-2 font-semibold uppercase tracking-wide text-black-50">
                {{ t('listings.smartSearchDropdown.developers') }}
              </p>
              <ul class="space-y-0.5">
                <li
                  v-for="dev in displayDevelopers"
                  :key="dev.id"
                  class="smart-search-row flex cursor-pointer items-center gap-3 rounded-lg px-2.5 py-2 transition-colors"
                  :style="isActive('developer', dev.id) ? activeRowStyle : {}"
                  @mouseenter="setActive('developer', dev.id)"
                  @mousedown.prevent="onSelect('developer', dev.id, dev.name, dev)"
                >
                  <div class="size-9 flex-shrink-0 rounded-lg bg-black-08 flex items-center justify-center">
                    <UIIcon
                      name="building-05"
                      class="text-black-40 text-lg"
                    />
                  </div>
                  <div class="min-w-0 flex-1">
                    <p class="text-subhead-3 text-black-100 truncate">
                      {{ t('listings.smartSearchDropdown.projectsBy', { name: dev.name }) }}
                    </p>
                    <p class="text-caption-1 text-black-50">
                      {{ t('listings.smartSearchDropdown.filterByDeveloper') }}
                    </p>
                  </div>
                  <span
                    class="text-caption-1 text-gold-primary-100 hidden flex-shrink-0 font-medium tablet:inline-block"
                  >
                    {{ t('listings.smartSearchDropdown.showProjects') }}
                    <span
                      aria-hidden="true"
                      class="rtl:inline-block rtl:-scale-x-100"
                      >→</span
                    >
                  </span>
                </li>
              </ul>
            </section>
            <section
              v-if="displayAreas.length"
              class="border-t border-black-10 px-3 py-2"
            >
              <p class="text-caption-1 mb-2 font-semibold uppercase tracking-wide text-black-50">
                {{ t('listings.smartSearchDropdown.area') }}
              </p>
              <ul class="space-y-0.5">
                <li
                  v-for="area in displayAreas"
                  :key="area.id"
                  class="smart-search-row flex cursor-pointer list-none items-center gap-3 rounded-lg px-2.5 py-2 transition-colors"
                  :style="isActive('area', String(area.id)) ? activeRowStyle : {}"
                  @mouseenter="setActive('area', String(area.id))"
                  @mousedown.prevent="onSelect('area', String(area.id), area.title, area)"
                >
                  <div class="size-9 flex-shrink-0 rounded-lg bg-black-08 flex items-center justify-center">
                    <UIIcon
                      name="location"
                      class="text-black-40 text-lg"
                    />
                  </div>
                  <div class="min-w-0 flex-1">
                    <p class="text-subhead-3 text-black-100 truncate">{{ area.title }}</p>
                    <p class="text-caption-1 text-black-50">
                      {{
                        area.virtual
                          ? t('listings.smartSearchDropdown.suggestedArea')
                          : t('listings.smartSearchDropdown.openDistrict')
                      }}
                    </p>
                  </div>
                  <span
                    class="rtl:inline-block rtl:-scale-x-100 text-caption-1 text-gold-primary-100 flex-shrink-0 font-medium"
                    >→</span
                  >
                </li>
              </ul>
            </section>
          </template>
          <template v-else>
            <div class="px-4 py-8 text-center text-body-3 text-black-60">
              {{ t('listings.smartSearchDropdown.noMatches') }}
            </div>
          </template>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { useElementBounding } from '@vueuse/core';

import type { SmartSearchGroupedResult } from '@/composables/usePropertyAutocomplete';

const { t } = useI18n();

const props = defineProps<{
  visible: boolean;
  grouped: SmartSearchGroupedResult;
  loading: boolean;
  isFallbackSuggestions?: boolean;
  dropdownClass?: string;
  anchorEl: HTMLElement | null;
}>();

const anchorRef = toRef(props, 'anchorEl');
const { bottom, left, width, update: updateAnchorBounds } = useElementBounding(anchorRef);

const dropdownStyle = computed(() => {
  if (!props.visible || !props.anchorEl) return {};
  return {
    top: `${bottom.value + 4}px`,
    left: `${left.value}px`,
    width: `${width.value}px`,
  };
});

const emit = defineEmits<{
  (e: 'select', payload: { type: 'project' | 'developer' | 'area'; id: string; name: string; raw?: unknown }): void;
}>();

const MAX_PROJECTS = 8;
const MAX_DEVELOPERS = 3;
const MAX_AREAS = 5;

const displayProjects = computed(() => props.grouped.projects.slice(0, MAX_PROJECTS));
const displayDevelopers = computed(() => props.grouped.developers.slice(0, MAX_DEVELOPERS));
// Drop areas the API explicitly marked as having 0 projects; unknown counts are allowed through
const displayAreas = computed(() => props.grouped.areas.filter((a) => a.projects_count !== 0).slice(0, MAX_AREAS));

const hasResults = computed(
  () => displayProjects.value.length > 0 || displayDevelopers.value.length > 0 || displayAreas.value.length > 0,
);

const onSelect = (type: 'project' | 'developer' | 'area', id: string, name: string, raw: unknown) => {
  emit('select', { type, id, name, raw });
};

// ── Hover + keyboard highlight ────────────────────────────────
const activeRowStyle = {
  backgroundColor: '#FDF6EC',
  boxShadow: 'inset 2px 0 0 0 #F5A623',
};

// Flattened list of all visible rows in DOM order for keyboard nav
type NavItem = { type: 'project' | 'developer' | 'area'; id: string; name: string; raw: unknown };
const allItems = computed<NavItem[]>(() => [
  ...displayProjects.value.map((p) => ({ type: 'project' as const, id: p.id, name: p.title, raw: p })),
  ...displayDevelopers.value.map((d) => ({ type: 'developer' as const, id: d.id, name: d.name, raw: d })),
  ...displayAreas.value.map((a) => ({ type: 'area' as const, id: String(a.id), name: a.title, raw: a })),
]);

const activeIndex = ref(-1);

const setActive = (type: string, id: string) => {
  activeIndex.value = allItems.value.findIndex((i) => i.type === type && i.id === id);
};

const isActive = (type: string, id: string) =>
  activeIndex.value >= 0 &&
  allItems.value[activeIndex.value]?.type === type &&
  allItems.value[activeIndex.value]?.id === id;

const handleKeydown = (e: KeyboardEvent) => {
  if (!props.visible) return;
  const len = allItems.value.length;
  if (!len) return;
  if (e.key === 'ArrowDown') {
    e.preventDefault();
    activeIndex.value = activeIndex.value < len - 1 ? activeIndex.value + 1 : 0;
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    activeIndex.value = activeIndex.value > 0 ? activeIndex.value - 1 : len - 1;
  } else if (e.key === 'Enter' && activeIndex.value >= 0) {
    e.preventDefault();
    const item = allItems.value[activeIndex.value];
    if (item) onSelect(item.type, item.id, item.name, item.raw);
  } else if (e.key === 'Escape') {
    activeIndex.value = -1;
  }
};

watch(
  () => props.visible,
  (visible) => {
    // `immediate: true` would fire on SSR where `document` is undefined.
    // Guard so the keyboard listener only attaches in the browser.
    if (!import.meta.client) return;
    if (visible) {
      // Re-measure the anchor every time the dropdown opens. useElementBounding
      // only re-fires on scroll/resize, so after a client-side navigation
      // (explore → dashboard → explore) the cached bounds can still be the
      // 0×0 it captured before the re-mounted layout settled — which renders
      // the teleported dropdown at width:0 (invisible). Measuring on open
      // guarantees a correct position regardless of when the component mounted.
      nextTick(updateAnchorBounds);
      document.addEventListener('keydown', handleKeydown);
    } else {
      document.removeEventListener('keydown', handleKeydown);
      activeIndex.value = -1;
    }
  },
  { immediate: true },
);

onUnmounted(() => {
  if (!import.meta.client) return;
  document.removeEventListener('keydown', handleKeydown);
});
</script>

<style scoped>
.smart-search-dropdown-root {
  height: auto !important;
  min-height: 0 !important;
}

.smart-search-dropdown-inner {
  max-height: none;
}
</style>
